// Create / edit a hub event in a modal. Closure/reduced/expansion scale the
// affected capacity; reroute additionally redistributes displaced volume to
// other hubs. An event can apply to the whole hub ("flat") or to specific
// customers ("by_customer") — e.g. reroute one customer's lane only.

import { useMemo, useState } from "react";
import { Button, Field, Input, Modal, Select, Textarea } from "../../components/ui";
import MultiSelect from "../../components/MultiSelect";
import { getErrorMessage } from "../../lib/errors";
import { parseOptionalNumber } from "../../lib/validation";
import { fmtN, today } from "../../lib/format";
import type {
  Customer,
  CustomerEventShare,
  HubEvent,
  HubEventInput,
  HubEventMode,
  HubEventType,
  Hub,
  RedistributeShare,
} from "../../lib/types";
import { createHubEvent, updateHubEvent } from "./hubEventsApi";

const TYPES: { id: HubEventType; label: string }[] = [
  { id: "closure", label: "Closure" },
  { id: "reduced", label: "Reduced capacity" },
  { id: "expansion", label: "Expansion" },
  { id: "reroute", label: "Reroute" },
  { id: "weather", label: "Weather" },
  { id: "outage", label: "Outage" },
  { id: "other", label: "Other" },
];

// Per-type behavior: what the capacity field means, and a plain-words blurb.
//  - "closure":  capacity is forced to 0 (no input).
//  - "required": the user must enter a capacity % (the event would be a no-op otherwise).
//  - "none":     informational — no capacity, no effect on volume.
const TYPE_INFO: Record<HubEventType, { blurb: string; capacity: "closure" | "required" | "none" }> = {
  closure: { blurb: "Hub is fully closed — 0% of volume is handled.", capacity: "closure" },
  reduced: {
    blurb: "Hub runs below normal — enter the % of volume it can still handle (under 100).",
    capacity: "required",
  },
  expansion: {
    blurb: "Hub runs above normal — enter the % it can handle (over 100, e.g. 120).",
    capacity: "required",
  },
  reroute: {
    blurb: "Move volume to other hubs — enter the % kept here (0 = move it all), then list the destinations below.",
    capacity: "required",
  },
  weather: { blurb: "Informational only — does not change forecast volume.", capacity: "none" },
  outage: { blurb: "Informational only — does not change forecast volume.", capacity: "none" },
  other: { blurb: "Informational only — does not change forecast volume.", capacity: "none" },
};

/** A numbered section header for the form's steps. */
function Step({ n, title }: { n: number; title: string }) {
  return (
    <div className="flex items-center gap-2 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-[11px] font-semibold text-white">
        {n}
      </span>
      <span className="text-sm font-semibold text-slate-700">{title}</span>
    </div>
  );
}

interface ShareDraft {
  hubId: string;
  pct: string;
}

interface Props {
  accountId: string;
  event: HubEvent | null;
  hubs: Hub[];
  customers: Customer[];
  initial?: Partial<HubEventInput>; // pre-fill a new event (e.g. from a suggestion)
  onClose: () => void;
  onSaved: () => void;
}

export default function HubEventForm({
  accountId,
  event,
  hubs,
  customers,
  initial,
  onClose,
  onSaved,
}: Props) {
  const seed = event ?? initial ?? null;
  const [hubId, setHubId] = useState(seed?.hubId ?? hubs[0]?.id ?? "");
  const [type, setType] = useState<HubEventType>(seed?.type ?? "reduced");
  const [startDate, setStartDate] = useState(seed?.startDate ?? today());
  const [endDate, setEndDate] = useState(seed?.endDate ?? today());
  const [capacity, setCapacity] = useState(seed?.capacityPct != null ? String(seed.capacityPct) : "");
  const [mode, setMode] = useState<HubEventMode>(seed?.mode ?? "flat");
  const [customerIds, setCustomerIds] = useState<string[]>(
    (seed?.customerOverrides ?? []).map((o) => o.customerId)
  );
  const [redistribute, setRedistribute] = useState(seed?.redistributeVolume ?? false);
  const [shares, setShares] = useState<ShareDraft[]>(
    (seed?.redistributeHubs ?? []).map((s) => ({ hubId: s.hubId, pct: String(s.pct) }))
  );
  const [notes, setNotes] = useState(seed?.notes ?? "");
  const [active, setActive] = useState(seed?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const capInfo = TYPE_INFO[type];
  const capacityRequired = capInfo.capacity === "required";

  // Customers allocated to the selected hub — the candidates an event can target.
  const hubCustomers = useMemo(
    () => customers.filter((c) => c.active && c.allocations.some((a) => a.hubId === hubId)),
    [customers, hubId]
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!hubId) return setError("Choose a hub.");
    if (endDate < startDate) return setError("End date must be on or after the start date.");

    // Build per-customer shares (blank % = the whole customer, i.e. 100%).
    let customerOverrides: CustomerEventShare[] | null = null;
    if (mode === "by_customer") {
      const sel = [...new Set(customerIds.filter((id) => id && hubCustomers.some((c) => c.id === id)))];
      if (sel.length === 0) {
        return setError("Add at least one customer, or set the event to apply to the whole hub.");
      }
      // Selected customers are affected in full (pct 100); the capacity % is the magnitude.
      customerOverrides = sel.map((id) => ({ customerId: id, pct: 100 }));
    }

    let capacityPct: number | null = null;
    if (capInfo.capacity === "closure") {
      capacityPct = 0;
    } else if (capInfo.capacity === "required") {
      const c = parseOptionalNumber(capacity);
      if (!c.ok || c.value === null) return setError(`Enter a capacity % for a ${type} event.`);
      const v = c.value;
      if (type === "reduced" && (v < 0 || v >= 100))
        return setError("Reduced capacity must be 0–99% (use Expansion for over 100%).");
      if (type === "expansion" && (v <= 100 || v > 300))
        return setError("Expansion capacity must be over 100% (e.g. 120).");
      if (type === "reroute" && (v < 0 || v >= 100))
        return setError("Reroute: enter the % kept here — 0–99 (0 = move it all).");
      capacityPct = Math.round(v);
    }
    // info-only types (weather/outage/other) leave capacityPct null → no volume effect

    // A reroute always sends volume somewhere; other types can optionally do so.
    const wantDest = type === "reroute" || redistribute;
    let redistributeHubs: RedistributeShare[] | null = null;
    if (wantDest) {
      const rows = shares.filter((s) => s.hubId);
      if (rows.length === 0) return setError("Add at least one destination hub.");
      let total = 0;
      const out: RedistributeShare[] = [];
      for (const r of rows) {
        if (r.hubId === hubId) return setError("A destination can't be the source hub.");
        const p = parseOptionalNumber(r.pct);
        if (!p.ok || p.value === null || p.value < 0 || p.value > 100) {
          return setError("Each destination share must be 0–100%.");
        }
        total += p.value;
        out.push({ hubId: r.hubId, pct: Math.round(p.value) });
      }
      if (total > 100) return setError(`Destination shares total ${total}% — can't exceed 100%.`);
      redistributeHubs = out;
    }

    const input: HubEventInput = {
      hubId,
      type,
      startDate,
      endDate,
      capacityPct,
      mode,
      customerOverrides,
      redistributeVolume: wantDest,
      redistributeHubs,
      active,
      notes,
    };
    setBusy(true);
    setError(null);
    try {
      if (event) await updateHubEvent(accountId, event.id, input);
      else await createHubEvent(accountId, input);
      onSaved();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to save event."));
    } finally {
      setBusy(false);
    }
  }

  // Derived values for the Keep/Move readout, destination running-total, and the
  // live "effect" preview (expressed per 1,000 affected units).
  const codeOf = (id: string) => hubs.find((h) => h.id === id)?.code ?? "?";
  const retainNum = capInfo.capacity === "closure" ? 0 : capacity.trim() === "" ? null : Number(capacity);
  const showDest = (type === "reroute" || redistribute) && capInfo.capacity !== "none";
  const destTotal = shares.reduce((t, s) => t + (Number(s.pct) || 0), 0);

  function effectPreview(): string {
    if (capInfo.capacity === "none") return "Informational — forecast volume is unchanged.";
    const r = retainNum;
    if (r == null || Number.isNaN(r)) return "Enter a capacity % to preview the effect.";
    const EX = 1000;
    const kept = Math.round((EX * r) / 100);
    if (type === "expansion") return `Each 1,000 units here → ${fmtN(kept)} (×${(r / 100).toFixed(2)}).`;
    const moved = Math.max(0, EX - kept);
    if (type !== "reroute") {
      return `Per 1,000 here: keep ${fmtN(kept)}, ${redistribute ? "move" : "lose"} ${fmtN(moved)}.`;
    }
    const dests = shares.filter((s) => s.hubId);
    const destStr = dests
      .map((s) => `${codeOf(s.hubId)} +${fmtN(Math.round((moved * (Number(s.pct) || 0)) / 100))}`)
      .join(", ");
    const sent = dests.reduce((t, s) => t + Math.round((moved * (Number(s.pct) || 0)) / 100), 0);
    const lost = moved - sent;
    let s = `Per 1,000 here: keep ${fmtN(kept)}, move ${fmtN(moved)}`;
    if (destStr) s += ` → ${destStr}`;
    if (lost > 0) s += ` (${fmtN(lost)} unallocated)`;
    return s + ".";
  }

  return (
    <Modal title={event ? "Edit hub event" : "New hub event"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Step n={1} title="What & when" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hub *">
            <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Type">
            <Select value={type} onChange={(e) => setType(e.target.value as HubEventType)}>
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Start *">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="End *">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
        <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">{capInfo.blurb}</p>

        <Step n={2} title="Who it affects" />
        <Field label="Applies to">
          <Select value={mode} onChange={(e) => setMode(e.target.value as HubEventMode)}>
            <option value="flat">Whole hub (all customers)</option>
            <option value="by_customer">Specific customers</option>
          </Select>
        </Field>

        {mode === "by_customer" && (
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            {hubCustomers.length === 0 ? (
              <p className="text-sm text-slate-500">
                No active customers are allocated to this hub.
              </p>
            ) : (
              <>
                <p className="text-xs text-slate-500">
                  Search and add the customers this event applies to at{" "}
                  {hubs.find((h) => h.id === hubId)?.name ?? "the hub"}. The effect in Step 3 applies
                  to their full volume here.
                </p>
                <MultiSelect
                  options={hubCustomers.map((c) => ({ id: c.id, label: c.name }))}
                  selected={customerIds.filter((id) => hubCustomers.some((c) => c.id === id))}
                  onChange={setCustomerIds}
                  placeholder="Search customers…"
                />
              </>
            )}
          </div>
        )}

        <Step n={3} title={type === "reroute" ? "Capacity kept here" : "Effect on volume"} />
        {capacityRequired ? (
          <div>
            <Field
              label={
                type === "reroute"
                  ? "Capacity % kept here *"
                  : type === "expansion"
                    ? "Capacity % (over 100) *"
                    : "Capacity % retained *"
              }
            >
              <Input
                type="number"
                min={0}
                max={300}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                placeholder={
                  type === "reroute" ? "0 = move all" : type === "expansion" ? "e.g. 120" : "e.g. 60"
                }
              />
            </Field>
            {retainNum != null && !Number.isNaN(retainNum) && (
              <p className="mt-1 text-xs text-slate-500">
                {type === "expansion"
                  ? `→ ${retainNum - 100 >= 0 ? "+" : ""}${retainNum - 100}% extra volume here`
                  : `Keep ${retainNum}% here · ${type === "reroute" ? "move" : "lose"} ${Math.max(
                      0,
                      100 - retainNum
                    )}%`}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-400">
            {type === "closure"
              ? "Capacity is fixed at 0% for a closure (all volume leaves)."
              : "No capacity change — this event is informational."}
          </p>
        )}

        {/* Non-reroute types can optionally send their displaced volume elsewhere. */}
        {capInfo.capacity !== "none" && type !== "reroute" && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={redistribute}
              onChange={(e) => setRedistribute(e.target.checked)}
            />
            Also send the displaced volume to other hubs
          </label>
        )}

        {showDest && (
          <>
            <Step n={4} title="Where it goes" />
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500">
                Each % is a share of the volume that <em>leaves</em> this hub (should total 100%).
              </p>
              {shares.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Select
                    value={s.hubId}
                    onChange={(e) =>
                      setShares((p) => p.map((x, j) => (j === i ? { ...x, hubId: e.target.value } : x)))
                    }
                  >
                    <option value="">Destination hub…</option>
                    {hubs
                      .filter((h) => h.id !== hubId)
                      .map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.name}
                        </option>
                      ))}
                  </Select>
                  <div className="w-20">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={s.pct}
                      placeholder="%"
                      onChange={(e) =>
                        setShares((p) => p.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShares((p) => p.filter((_, j) => j !== i))}
                  >
                    ✕
                  </Button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShares((p) => [...p, { hubId: "", pct: "" }])}
                >
                  + Add destination
                </Button>
                <span
                  className={`text-xs ${
                    destTotal === 100
                      ? "text-emerald-600"
                      : destTotal > 100
                        ? "text-red-600"
                        : "text-amber-600"
                  }`}
                >
                  {destTotal === 100
                    ? "100% allocated ✓"
                    : destTotal > 100
                      ? `${destTotal}% — over 100%`
                      : `${destTotal}% allocated · ${100 - destTotal}% unallocated`}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Live, plain-words preview of what the event does. */}
        <div className="rounded-md border border-brand-100 bg-brand-50/60 px-3 py-2 text-xs text-slate-700">
          <span className="font-medium text-slate-800">Effect:</span> {effectPreview()}
          {mode === "by_customer" && capInfo.capacity !== "none" && (
            <span className="text-slate-500"> Applied to the selected customers' volume here.</span>
          )}
        </div>

        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving…" : event ? "Save changes" : "Create event"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
